/**
 * "Tracks this punter has hearted" — purely local, per-device, like `myRequests.ts`. Unlike
 * requests (one append per request event, duplicates expected), a like is a toggle keyed by
 * `smId` — liking twice is a no-op-then-undo, not two entries.
 */

const STORAGE_KEY = "touchscreen:liked_tracks";
const CHANGE_EVENT = "touchscreen:liked-tracks-changed";
const MAX_ENTRIES = 200;

export interface LikedTrackEntry {
  smId: string;
  likedAtMs: number;
}

// See `myRequests.ts` for why `getSnapshot` needs a stable reference when nothing changed.
let cachedRaw: string | null = null;
let cachedList: LikedTrackEntry[] = [];

function readList(): LikedTrackEntry[] {
  if (typeof window === "undefined") return cachedList;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return cachedList;
  }
  if (raw === cachedRaw) return cachedList;
  cachedRaw = raw;
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    cachedList = Array.isArray(parsed) ? (parsed as LikedTrackEntry[]) : [];
  } catch {
    cachedList = [];
  }
  return cachedList;
}

function writeList(list: LikedTrackEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage full or blocked — this is a convenience list, nothing depends on it persisting.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Newest first. Returns the new liked state (`true` if now liked, `false` if just unliked). */
export function toggleLike(smId: string): boolean {
  const list = readList();
  const existing = list.filter((entry) => entry.smId !== smId);
  if (existing.length !== list.length) {
    writeList(existing);
    return false;
  }
  writeList([{ smId, likedAtMs: Date.now() }, ...list].slice(0, MAX_ENTRIES));
  return true;
}

export function isLiked(smId: string): boolean {
  return readList().some((entry) => entry.smId === smId);
}

export function getLikedTracks(): LikedTrackEntry[] {
  return readList();
}

/** Notifies same-tab writers (`storage` alone only fires cross-tab) so screens/buttons update live. */
export function subscribeLikedTracks(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}
